import React, { ReactElement, useMemo } from 'react';

import { QueryResult } from '@apollo/client';

import { ContactsAndSettingsPermissions } from '@app/queryTyping';

import { Field } from '@app/common/configurable-wizards/types';
import { createGenericContext } from '@app/common/contexts/createGenericContext';

import {
  useGetContactsAndSettingsQuery,
} from '@app/widgets/contacts-and-settings/queries/queryTyping/getContactsAndSettings';
import {
  useGetContactsEmptyFieldsQuery,
} from '@app/widgets/contacts-and-settings/queries/queryTyping/getContactsEmptyFields';
import { getSettingsRoutes, RouteItem } from '@app/widgets/contacts-and-settings/utils/settingsRoutes';
import {
  getSettingsSections,
  RenderSection,
  RenderSectionGroup,
} from '@app/widgets/contacts-and-settings/utils/settingsSections';

export interface ContactSettingsData {
  readonly fields: Field[];
  readonly emptyFields: Record<string, Field>;
  readonly settingsQuery: Omit<QueryResult, 'data' | 'loading' | 'error'>;
  readonly loading: boolean;
  readonly sections: RenderSection[];
  readonly sectionGroups: RenderSectionGroup[];
  readonly routes: RouteItem[];
  readonly paths: string[];
  readonly permissions: ContactsAndSettingsPermissions;
}

export interface SettingFieldsProviderProps {
  /**
   * Render provider's children callback
   */
  readonly renderer?: (props: ContactSettingsData) => ReactElement;
}

/**
 * Context to query contacts & settings.
 */
const [useSettingFieldsContext, SettingFieldsContextProvider] = createGenericContext<ContactSettingsData>();

/**
 * Contacts & settings context provider.
 */
const SettingFieldsProvider: React.FC<SettingFieldsProviderProps> = ({ renderer }) => {
  const {
    data: settingsFieldsData,
    loading: settingsLoading,
    error: settingsError,
    ...settingsQuery
  } = useGetContactsAndSettingsQuery({
    fetchPolicy: 'no-cache',
  });
  const {
    data: emptyFieldsData,
    loading: emptyFieldsLoading,
    error: emptyFieldsError,
  } = useGetContactsEmptyFieldsQuery({
    fetchPolicy: 'no-cache',
  });

  const loading = settingsLoading || emptyFieldsLoading;
  const error = settingsError || emptyFieldsError;
  const fields = settingsFieldsData?.contactsAndSettings.fields as Field[];
  const emptyFields = emptyFieldsData?.contactsEmptyFields as unknown as Record<string, Field>;
  const permissions = settingsFieldsData?.contactsAndSettings.config as ContactsAndSettingsPermissions;

  const sectionGroups = useMemo(
    () => getSettingsSections(fields),
    [settingsFieldsData, loading],
  );

  const sections = useMemo(() => sectionGroups.reduce<Array<RenderSection>>(
    (prev, curr) => [...prev, ...curr.sections],
    [],
  ), [sectionGroups]);

  const routes = useMemo(() => {
    const ids = sections.map(({ id }) => id);

    return getSettingsRoutes(ids.join('|'));
  }, [sections]);

  const paths = useMemo(
    () => routes.map(({ path }) => path),
    [routes],
  );

  const ctxValue: ContactSettingsData = {
    loading,
    fields,
    emptyFields,
    settingsQuery,
    routes,
    paths,
    sections,
    sectionGroups,
    permissions,
  };

  if (error) {
    throw error;
  }

  return (
    <SettingFieldsContextProvider value={ctxValue}>
      {renderer && renderer(ctxValue)}
    </SettingFieldsContextProvider>
  );
};

export { useSettingFieldsContext, SettingFieldsProvider };
